import DownChevron from "@client/icons/DownChevron";
import { EntityType, FieldResponse } from "@engine/round/aggregate";
import * as Sentry from "@sentry/react";
import { useLocalStorage } from "@uidotdev/usehooks";
import clsx from "clsx";
import { Fragment } from "react";
import { FieldContent } from "./FieldContent";
import {
  alteredSectionHeaders,
  INITIAL_SECTION_ID,
  Section,
  sectionGroupMapping,
} from "./sectionUtils";

export function UntypedFieldResponsesInfo({
  fieldResponses,
  entityType,
  requestsMatchingPledge,
  allCollapsedSections,
  toggleSectionVisibility,
  isAppInfoCollapsed,
  sections,
  toggleGroupVisibility,
}: {
  fieldResponses: Record<string, FieldResponse>;
  entityType: EntityType;
  requestsMatchingPledge: boolean;
  allCollapsedSections: Record<string, boolean>;
  toggleSectionVisibility: (sectionId: string) => void;
  toggleGroupVisibility: (groupHeaderTitle: string) => void;
  isAppInfoCollapsed: boolean;
  sections: Section[];
}) {
  // Description collapse toggles are independent of section-level toggles, so
  // we keep their persisted state separate.
  const [hiddenDescriptions, setHiddenDescriptions] = useLocalStorage<
    Record<string, boolean>
  >("descriptionState", {});

  const toggleDescriptionVisibility = (fieldId: string) => {
    setHiddenDescriptions((prev) => ({
      ...prev,
      [fieldId]: !prev[fieldId],
    }));
  };

  // Apply domain-specific filtering (matching pledge, for-profit, etc.)
  const filteredSections = sections.filter((section) => {
    return (
      sectionIsRelevant(
        section,
        entityType,
        requestsMatchingPledge,
        fieldResponses,
      ) || sectionContainsAnyResponses(section, fieldResponses)
    );
  });

  return (
    <>
      {filteredSections.map((section) => {
        const isSectionCollapsed = allCollapsedSections[section.id];
        const displayTitle = section.header
          ? alteredSectionHeaders[section.header.title] || section.header.title
          : "";

        const isThisSectionContentCollapsed =
          section.id === INITIAL_SECTION_ID
            ? isAppInfoCollapsed
            : isSectionCollapsed;

        const isGroupHeader =
          section.header && sectionGroupMapping[section.header.title];

        const handleClick = () => {
          if (!section.header) {
            // Should not be clickable if no header
            Sentry.captureMessage(
              `Section without a known group header title: ${section}`,
              "warning",
            );
            return;
          }
          if (isGroupHeader) {
            toggleGroupVisibility(section.header.title);
          } else {
            toggleSectionVisibility(section.id);
          }
        };

        return (
          <Fragment key={section.id}>
            {/* Section header */}
            {section.header && (
              <div
                className={clsx(
                  "flex items-center my-4",
                  !isGroupHeader && "cursor-pointer",
                  section.indent && "ml-4",
                )}
                onClick={!isGroupHeader ? handleClick : undefined}
              >
                {!isGroupHeader && (
                  <div
                    className={clsx(
                      "shrink-0 w-5 h-5 fill-stone-700 dark:fill-white transform text-stone-200",
                      isThisSectionContentCollapsed ? "-rotate-90" : "",
                    )}
                  >
                    <DownChevron />
                  </div>
                )}
                <h4 className={clsx("m-0", isGroupHeader && "font-bold")}>
                  {displayTitle}
                </h4>
              </div>
            )}

            {/* Section content */}
            <div
              className={clsx(
                "overflow-hidden ml-4",
                isThisSectionContentCollapsed ? "max-h-0" : "",
                section.header ? "pl-5" : "",
              )}
            >
              {section.longFormWarning}
              {section.fields.map((field) => (
                <FieldContent
                  key={field.id}
                  field={field}
                  fieldResponse={fieldResponses[field.id]}
                  descriptionHidden={hiddenDescriptions[field.id]}
                  toggleDescriptionVisibility={toggleDescriptionVisibility}
                  inSpecialSection={section.specialSectionType}
                />
              ))}
            </div>
          </Fragment>
        );
      })}
    </>
  );
}

function sectionContainsAnyResponses(
  section: Section,
  fieldResponses: Record<string, FieldResponse>,
) {
  const hasResponse = section.fields.some((f) => {
    const resp = fieldResponses[f.id];
    return resp && resp.type === "question" && resp.value !== "";
  });
  return hasResponse;
}

function sectionIsRelevant(
  section: Section,
  entityType: EntityType,
  requestsMatchingPledge: boolean,
  fieldResponses: Record<string, FieldResponse>,
) {
  if (
    section.specialSectionType === "for-profit" &&
    entityType === "non-profit"
  ) {
    return false;
  }
  if (
    section.specialSectionType === "non-profit" &&
    (entityType === "for-profit" || entityType === "social-welfare")
  ) {
    return false;
  }
  if (section.specialSectionType === "matching" && !requestsMatchingPledge) {
    return false;
  }
  if (section.specialSectionType === "longform") {
    const hasResponse = section.fields.some((f) => {
      const resp = fieldResponses[f.id];
      return resp && resp.type === "question" && resp.value !== "";
    });
    return hasResponse;
  }
  return true;
}
